import type { DisruptionOverviewRow, DisruptionSeverity } from '@/lib/disruptions/types';
import { countryOf, onActiveMap } from './airport-geo';

// Pure view logic for the /disruptions situation board: the operator's scope
// (network / region / airport), the KPI fold and the per-airport aggregates the
// map draws. All derived client-side from the ONE ranked overview payload — the
// per-disruption rollups stay server-denormalized (§5); this only sums/filters
// them per the operator's chosen lens, so scope switches are instant (no refetch).

export type DisruptionsScope =
  | { kind: 'network' }
  | { kind: 'region'; country: string }
  | { kind: 'airport'; airport: string };

export const NETWORK_SCOPE: DisruptionsScope = { kind: 'network' };

/** `region:Argentina` / `airport:AEP` → scope; null/garbage → null (caller defaults). */
export function parseScope(raw: string | null): DisruptionsScope | null {
  if (!raw) return null;
  if (raw === 'network') return NETWORK_SCOPE;
  const [kind, ...rest] = raw.split(':');
  const value = rest.join(':').trim();
  if (kind === 'region' && value) return { kind: 'region', country: value };
  if (kind === 'airport' && value) return { kind: 'airport', airport: value.toUpperCase() };
  return null;
}

export function scopeToParam(scope: DisruptionsScope): string | null {
  if (scope.kind === 'region') return `region:${scope.country}`;
  if (scope.kind === 'airport') return `airport:${scope.airport}`;
  return null; // network is the default → keep the URL clean
}

/** Friendly IATA codes from the row's scope URNs (`urn:...:airport:SCL` → `SCL`). */
export function rowAirportCodes(row: DisruptionOverviewRow): string[] {
  return (row.scope.airports ?? [])
    .map((urn) => (urn.split(':').pop() ?? '').toUpperCase())
    .filter(Boolean);
}

/**
 * Scope membership. Rows with NO airports (airline-wide disruptions) concern
 * every lens, so they stay visible in every scope rather than vanishing when
 * the operator narrows.
 */
export function rowInScope(row: DisruptionOverviewRow, scope: DisruptionsScope): boolean {
  if (scope.kind === 'network') return true;
  const codes = rowAirportCodes(row);
  if (codes.length === 0) return true;
  if (scope.kind === 'airport') return codes.includes(scope.airport);
  return codes.some((code) => countryOf(code) === scope.country);
}

/**
 * Membership against the HEADER airport scope (NEX-310) — a concrete IATA set,
 * [] = no narrowing. Same airline-wide rule as {@link rowInScope}: rows with no
 * airports concern every lens and stay visible.
 */
export function rowInAirports(row: DisruptionOverviewRow, airports: readonly string[]): boolean {
  if (airports.length === 0) return true;
  const codes = rowAirportCodes(row);
  if (codes.length === 0) return true;
  return codes.some((code) => airports.includes(code));
}

const SEVERITY_WEIGHT: Record<DisruptionSeverity, number> = {
  critical: 3,
  warning: 2,
  active: 1,
  settled: 0,
};

export function worstSeverity(a: DisruptionSeverity, b: DisruptionSeverity): DisruptionSeverity {
  return SEVERITY_WEIGHT[a] >= SEVERITY_WEIGHT[b] ? a : b;
}

/** What the KPI band shows for the current scope. */
export interface SituationKpis {
  open: number;
  attention: number;
  affected: number;
  unresolved: number;
  /** Σ the rows' hotel spend at the airline's PRICE (`airline-money@v2`). `undefined` when
   *  no row carries an amount — the overview rows don't (their S4 estimate is structurally
   *  unpriceable), so the board shows "—"; `null` when some row's price is not available
   *  (a partial sum would read as a total). Nothing sorts or filters by it. */
  priceMinor?: number | null;
  currency: string;
}

/** One airport's fold — a map marker / tray chip. */
export interface AirportSituation {
  code: string;
  country?: string;
  disruptions: number;
  attention: number;
  affected: number;
  unresolved: number;
  severity: DisruptionSeverity;
  onMap: boolean;
}

export function kpisOf(rows: DisruptionOverviewRow[]): SituationKpis {
  const kpis: SituationKpis = {
    open: rows.length,
    attention: 0,
    affected: 0,
    unresolved: 0,
    currency: rows.find((r) => r.rollup.currency)?.rollup.currency ?? 'USD',
  };
  for (const row of rows) {
    if (row.severity === 'critical' || row.severity === 'warning') kpis.attention += 1;
    kpis.affected += row.rollup.affectedPassengers ?? 0;
    kpis.unresolved += row.unresolvedPassengers;
    const price = row.rollup.totalPriceMinor;
    if (price === undefined) continue; // no amount on this row (overview) — nothing to add
    kpis.priceMinor =
      kpis.priceMinor === null || price === null ? null : (kpis.priceMinor ?? 0) + price;
  }
  return kpis;
}

/**
 * Fold rows → per-airport situation. A multi-airport disruption counts at each
 * of its airports (the marker answers "what is happening HERE"), so airport
 * figures are a local lens, not a partition of the network totals.
 */
export function airportSituations(rows: DisruptionOverviewRow[]): AirportSituation[] {
  const byCode = new Map<string, AirportSituation>();
  for (const row of rows) {
    for (const code of rowAirportCodes(row)) {
      const country = countryOf(code);
      const prev =
        byCode.get(code) ??
        ({
          code,
          ...(country ? { country } : {}),
          disruptions: 0,
          attention: 0,
          affected: 0,
          unresolved: 0,
          severity: 'settled',
          onMap: onActiveMap(code),
        } as AirportSituation);
      prev.disruptions += 1;
      if (row.severity === 'critical' || row.severity === 'warning') prev.attention += 1;
      prev.affected += row.rollup.affectedPassengers ?? 0;
      prev.unresolved += row.unresolvedPassengers;
      prev.severity = worstSeverity(prev.severity, row.severity);
      byCode.set(code, prev);
    }
  }
  return [...byCode.values()].sort(
    (a, b) =>
      SEVERITY_WEIGHT[b.severity] - SEVERITY_WEIGHT[a.severity] || b.unresolved - a.unresolved,
  );
}

/** Countries with any presence (operated airports ∪ rows), for the region picker. */
export function knownCountries(airportCodes: string[]): string[] {
  const set = new Set<string>();
  for (const code of airportCodes) {
    const country = countryOf(code);
    if (country) set.add(country);
  }
  return [...set].sort();
}
